/**
 * Copyright 2021-2022 Highway9 Networks Inc.
 */

import { CARRIER_MODE, CPI_TYPE, FREQUENCY_SELECTION_LOGIC } from "../constants";
import { ChannelQuality } from "./channelQuality";
import { DataObject, ObjectReference } from "./dataObject";


export default interface Radio extends DataObject {
  // availableUpgrades?: RadioAvailableUpgrads[];
  upgradeToRelease?: string;
  caCert?: string;
  installInfo?: InstallInfo;
  carrierAggregationEnabled?: boolean;
  /** @enum {string} "SINGLE_CARRIER" | "DUAL_CARRIER" | "CARRIER_AGGREGATION" */
  carrierMode?: string;
  cellParameters?: CellParameter[];
  creationTime?: number;
  description?: string;
  downTilt?: number;
  edgeConfig?: EdgeConfigItem[];
  edgeId?: string; // remove it from the API and use edge reference one for now , using it for internal references.
  edgeName?: string; // remove it from the API and use edge reference one
  edges?: EdgeReference[];
  /** @enum {string} "4G" | "5G"*/
  edgeNetworkType?: string;
  /** @enum {string} "BAD" | "GOOD" | "NA"; */
  health?: string;
  id?: string;
  docVersion?: number;
  /** @enum {string} "CONTROLLER" | "EDGE" | "RADIO"; */
  instanceType?: string;
  ipsecCert?: string;
  ipsecConfig?: IPSec;
  ipsecKey?: string;
  /** @enum {string} "PSK" | "CERTIFICATE" | "DISABLED" */
  ipsecMode?: string; // remove it from the API and use the edge reference
  /** @deprecated use serialNumber instead */
  isAddedToRadioManager?: boolean;
  isIpsecUpdateRequired?: boolean;
  isManaged?: boolean;
  securityForCiphering?: "ENABLED" | "AUTOMATIC";
  isPartnerRadio?: boolean;
  isSimulatedRadio?: boolean;
  localExit?: LocalExit;
  location?: Location;
  floorPlan?: RadioFloorPlan;
  /** @enum {string} "MANUAL" | "GNSS" | "PTP" */
  locationSyncMode?: string;
  mmeIp?: string;
  model?: string;
  name: string;
  plmnId?: string;
  rebootIfRequired?: boolean;
  readonly runtimeInfo?: RuntimeInfo;
  sasConfig?: SASConfig;
  serialNumber?: string;
  technology: string;
  serviceInstanceId?: string;
  serviceInstanceName?: string;
  smallCellCode?: string;
  /** @enum {string} "NOT_DEPLOYED"| "DEPLOYING"| "DEPLOYED"| "FAILED"| "MARKED_FOR_DELETE"| "DELETING"| "CLEAR_SERIAL_NUMBER"| "MARKED_FOR_REBOOT" | "WAITING"| "PROCESSING_WAITING" ; */
  type?: string;
  updatedTime?: number;
  radioGroup?: ObjectReference;
  radioSite?: ObjectReference;
  plan?: {
    mapPoints?: {
      latitude: number;
      longitude: number;
    };
  };
  neighborsAutoAssign?: boolean;
  isBorderRadio?: boolean;
  neighborList?: {
    id: string;
    cellParameters?: {
      cellIndex: number;
      sourceCell: string | undefined;
      earfcn?: number;
    }[];
  }[];
  activeSubscribers?: number;
  mobilityProfile?: ObjectReference;
  overrideEdges?: boolean;
  overrideLocationSyncMode?: boolean;
  modelType?: string;

  /** @enum {string} "BAICELLS" | "SIMULATOR" | "AIRSPAN" */
  vendorType?: string;
  simulatedAppliance?: ObjectReference;
  clockPool?: ObjectReference;
  isReleaseCompatible?: boolean;
  surveyInfo?: RadioSurveyInfo;
  operationalState?: OperationalState;
}

export enum OperationalState {
  IN_SERVICE = "IN_SERVICE",
  UNDER_MAINTENANCE = "UNDER_MAINTENANCE",
}

export enum RadioStatus {
  GOOD = "GOOD",
  BAD = "BAD",
  PARTIALLY_GOOD = "PARTIALLY_GOOD",
  THIRDPARTY = "THIRDPARTY",
  UNKNOWN = "UNKNOWN",
  DISABLED = "DISABLED",
}

/**
 * Extends Radio with additional fields
 * @extends Radio with key, statusColor, warnings
 */
export interface RadioX extends Radio {
  key: string | number;
  statusColor: string;
}

export interface EdgeReference {
  edge: {
    id: string;
    name?: string;
    /** @enum {string} "PRIVATE_CELLULAR" | "CARRIER_GATEWAY" */
    type?: string;
  };
  /** @enum {string} 'PSK' | 'CERTIFICATE' | "DISABLED"; */
  ipsecMode: string;
  carriers?: {
    id: string;
    name?: string;
    type?: string;
    reserved?: boolean;
  }[]
}

export interface RadioAvailableUpgrades {
  creationTime: Date | string;
  recommended: boolean;
  id?: string;
  vendorType: string;
  imageName?: string;
  release: string;
  runtimeInfo?: any;
  unsupported: boolean;
  model?: string;
}
export interface Location {
  edgeEnabled?: boolean;
  enabled?: boolean;
  id?: string;
  latitude?: number;
  /** @enum {string} "GCP" | "AWS" | "PRIVATE CLOUD" */
  locationType?: string;
  longitude?: number;
  /** @enum {string} "ROADMAP" | "SATELLITE" | "HYBRID" | "TERRAIN" */
  view?: string;
  name?: string;
  zoom?: number;
  readonly runtimeInfo?: any;
  tags?: string[];
  vmcEnabled?: boolean;
  zoneInfo?: {
    groupName?: string;
    regionName?: string;
  };
}

export interface InstallInfo {
  images?: {
    imageName: string;
    /** @enum {string} "MOUNT_IMAGE" | "SERIAL_NUMBER" */
    imageTag: string;
    imageData: string;
    imageId: string;
  }[];
  cablingInfo?: string;
  locationChangeReason?: string;
  installationNotes?: string;
}

export enum FEASIBILITY_STATE {
  NOT_KNOWN = "NOT_KNOWN",
  FEASIBLE = "FEASIBLE",
  NOT_FEASIBLE = "NOT_FEASIBLE",
  FEASIBLE_WITH_CHANGES = "FEASIBLE_WITH_CHANGES",
  PROPOSED_NEW = "PROPOSED_NEW",
}

interface RadioFloorPlan {
  xOffset: number;
  yOffset: number;
}

export interface ImageInfo {
  imageId?: string;
  imageName?: string;
  imageData?: string;
}

export interface RadioSurveyInfo {
  suggestedLocation: RadioFloorPlan;
  feasibilityState: FEASIBILITY_STATE;
  images: ImageInfo[];
  notes: string;
}

export interface LocalExit {
  enableStaticIp?: boolean;
  enabled?: boolean;
  ipPool?: string;
  /** @enum {string}  "255.255.255.0"| "255.255.255.128"| "255.255.255.192"| "255.255.255.224"| "255.255.255.240"| "255.255.255.248"; */
  ipPoolNetmask?: string;
  mode?: number;
  staticIpEnd?: string;
  staticIpStart?: string;
}
export interface RuntimeInfo {
  cellParameters?: RadioRuntimeInfoCellParameter[];
  connected?: boolean;
  softwareVersion?: string;
  connectedDevicesCount?: number;
  dataInBytes?: number;
  dataOutBytes?: number;
  readonly edgeConfig?: EdgeConfigRT[]; // read only
  edgeId?: string;
  RadioMonitoring?:{
    enabled: boolean
  };
  functionCapacity?: {
    carrierAggregationFunctionEnabled?: boolean;
    dualCarrierFunctionEnabled?: boolean;
  };
  url?: string;
  gpsStatus?: boolean; // @deprecated
  ipAddress?: string;
  ipsecIp?: string;
  interfaceIp?: string;
  lastSyncTime?: number;
  lastInformationTime?: number;
  synchronizationStatus?: string;
  readonly synchronizationInfo?: SyncInfo;
  macAddress?: string;
  mmeStatus?: MmeStatus[];
  plmnList?: string[];
  plmnId?: string;
  sas?: {
    isDoing?: boolean;
    state?: number;
  };
  tac?: string;
  mobilityParameters?: {
    enableX2?: boolean;
  };
  // uptime?: string;
  systemStartTime?: number;
  alarmCount: number;


  health?: RadioStatus;
  overallHealth?: RadioStatus;
  cloudStatus?: RadioStatus;
  edgeStatus?: {
    overallEdgeStatus: RadioStatus,
    perEdgeStatus: [
        {
            edgeId: string,
            status: RadioStatus
        }
    ]
  };
  rfStatus?: {
    overallRfStatus: RadioStatus,
    perCellStatus: [
        {
            cellIndex: number,
            status: RadioStatus
        },
        {
            cellIndex: number,
            status: RadioStatus
        }
    ]
  };
  freqStatus?: {
    overallFreqStatus: RadioStatus,
    perCellStatus: [
        {
            cellIndex: number,
            status: RadioStatus
        },
        {
            cellIndex: number,
            status: RadioStatus
        }
    ]
  };


  vendorType?: string;
  //Neighbor Details for AirSpan radios
  neighborInfo: {
    type: string;
    cellId: number;
    discoveredBy: string;
    earfcn: number;
    band: number;
    handoverType: string;
    id: string;
    name: string;
    neighborOfCell: string;
    pci: number;
    plmnList: string[];
    serialNumber: string;
    x2Status: string;
    status: string;
    tac: number;
  }[];
  radioManager?: {
    id: string
    type?: string
    name: string
    privateIp: string
  }
  neighborList?: {
    id: string;
    cellParameters?: {
      cellIndex: number;
      sourceCell?: string;
      earfcn?: number;
    }[];
  }[];
  snmpReadContext?: string;
  snmpWriteContext?: string;
  securityConfig?: {
    securityForCiphering?: string;
  };
}

export interface SASConfig {
  /** @enum {string}  "A" | "B" */
  cbsdCategory?: string;
  cpiData?: {
    /** @enum {string} "Operator" | "Thirdparty" */
    cpiType?: string;
    id?: string;
    installCertificationTime?: string;
    name?: string;
    signature?: string; // @deprecated
  };
  fccId?: string;
  /** @enum {string}  "Power,Bandwidth,Frequency"
    | "Power,Frequency,Bandwidth"
    | "Bandwidth,Frequency,Power"
    | "Bandwidth,Power,Frequency"
    | "Frequency,Power,Bandwidth"
    | "Frequency,Bandwidth,Power"; */
  frequencySelectionLogic?: string;
  isOnlyAllowSelectedFrequency?: boolean;
  sasEnabled?: boolean;
  userId?: string;
  overrideSasConfig?: boolean;
}

interface RadioRuntimeInfoCellParameter {
  bandwidth?: number;
  cellIndex?: number;
  earfcn?: number;
  power?: number;
  rfEnabled?: boolean;
  sas?: {
    isDoing?: boolean;
    state?: number;
  };
  rsi: number;
  channelQuality?: ChannelQuality;
  channelValue?: number;
}

export interface CellParameter {
  altitude?: number | null;
  azimuth: number;
  band: string;
  bandwidth: number;
  beamwidth: number;
  cellId?: number;
  cellIndex?: number;
  downTilt: number;
  earfcn: number[];
  gain: number;
  installCertificationTime?: Date;
  pci: number;
  power: number;
  rfEnabled: boolean;
  signature?: string;
  specialSubframePattern: number;
  subframeAssignment: number;
  tac: number;
  eciAutoAssign?: boolean;
  pciAutoAssign?: boolean;
  earfcnAutoAssign?: boolean;
  powerAutoAssign?: boolean;
  overrideBandwidth?: boolean;
}
export interface EdgeConfigItem {
  id?: string;
  ipsecConfig?: IPSec;
  plmnId?: string;
  mmeIp?: string;
  name?: string;
  status?: boolean;
}

interface IPSec {
  gateway?: string;
  leftAuth?: string;
  leftCert?: string;
  leftId?: string;
  leftSourceIp?: string;
  leftSubnet?: string;
  pskSecretKey?: string;
  rightAuth?: string;
  rightId?: string;
  rightSubnet?: string;
  secretKey?: string;
}

interface EdgeConfigRT {
  id: string;
  ipsecIpAddress: string;
  ipsecMode: string;
  ipsecStatus: string;
  ipsecDownReason?: string;
}

interface MmeStatus {
  ip?: string;
  status?: boolean;
  plmnId?: string;
}

export interface SyncInfo {
  ptp?: PtpInfo;
}

export interface PtpInfo {
  masterConnectivity: string;
  synchronizationStatus: string;
  holdoverReason: string;
  holdExpiredTime: number;
  activeMasterStatus: string;
  currentGrandMasterClockClass: number;
  currentUtcOffset: number;
}

type BandBandwidthMap = {
  [key: string]: number[];
};

export const bandBandwidthMap: BandBandwidthMap = {
  "B48": [5, 10, 15, 20],
  "N48": [5, 10, 15, 20, 30, 40],
  "N48_CLASS_M": [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
  "N77": [5, 10, 15, 20, 30, 40, 50, 60, 70, 80, 90, 100],
  "N78": [5, 10, 15, 20, 30, 40, 50, 60, 70, 80, 90, 100],
};

export const defaultRadio: Radio = {
  name: "",
  locationSyncMode: "MANUAL",
  technology: "4G",
  cellParameters: [
    {
      azimuth: 0,
      band: "B48",
      bandwidth: 20,
      beamwidth: 0,
      altitude: 0,
      cellIndex: 1,
      downTilt: 0,
      earfcn: [55340],
      gain: 0,
      pci: 31,
      power: 24,
      rfEnabled: true,
      specialSubframePattern: 7,
      subframeAssignment: 1,
      tac: 63,
      eciAutoAssign: true,
      pciAutoAssign: true,
      earfcnAutoAssign: true,
      powerAutoAssign: true,
      overrideBandwidth: false,
    },
    {
      azimuth: 0,
      band: "B48",
      bandwidth: 20,
      beamwidth: 0,
      altitude: 0,
      cellIndex: 2,
      downTilt: 0,
      earfcn: [55540],
      gain: 0,
      pci: 32,
      power: 24,
      rfEnabled: true,
      specialSubframePattern: 7,
      subframeAssignment: 1,
      tac: 63,
      eciAutoAssign: true,
      pciAutoAssign: true,
      earfcnAutoAssign: true,
      powerAutoAssign: true,
      overrideBandwidth: false,
    },
  ],
  sasConfig: {
    sasEnabled: false,
    cbsdCategory: "A",
    fccId: "",
    userId: "",
    isOnlyAllowSelectedFrequency: false,
    cpiData: {
      cpiType: CPI_TYPE.THIRD_PARTY,
      id: "",
      name: "",
    },
    frequencySelectionLogic: FREQUENCY_SELECTION_LOGIC.PBF,
  },
  rebootIfRequired: true,
  carrierMode: CARRIER_MODE.CARRIER_AGGREGATION,
  neighborList: [],
  neighborsAutoAssign: true,
  isBorderRadio: false,
  isManaged: true,
  securityForCiphering: "AUTOMATIC",
  operationalState: OperationalState.IN_SERVICE,
  localExit: {
    enabled: false,
    mode: 0,
    ipPool: "10.0.0.0",
    ipPoolNetmask: "255.255.255.0",
    enableStaticIp: false,
    staticIpStart: "10.0.0.1",
    staticIpEnd: "10.0.0.254",
  }
};
